import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading?: boolean;
}

const buildPageList = (
  currentPage: number,
  totalPages: number,
  windowSize = 2,
): (number | "...")[] => {
  if (totalPages <= 1) return [0];

  const pages: (number | "...")[] = [];
  const left = Math.max(0, currentPage - windowSize);
  const right = Math.min(totalPages - 1, currentPage + windowSize);

  pages.push(0);
  if (left > 1) pages.push("...");

  for (let i = left; i <= right; i++) {
    if (i !== 0 && i !== totalPages - 1) {
      pages.push(i);
    }
  }

  if (right < totalPages - 2) pages.push("...");

  if (totalPages > 1) pages.push(totalPages - 1);

  return pages;
};

const Pagination = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
}: PaginationProps) => {
  const { t } = useTranslation();

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const pageItems = useMemo(
    () => buildPageList(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const atFirstPage = currentPage === 0;
  const atLastPage = currentPage >= totalPages - 1;

  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-3 pb-2 border-t border-gray-200">
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600">
          {totalItems > 0
            ? `${startItem}-${endItem} ${t("statsNew.pagination.of")} ${totalItems}`
            : t("general.noResults")}
        </span>
        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isLoading}
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-600">{t("statsNew.pagination.perPage")}</span>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(0)}
            disabled={atFirstPage || isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {t("statsNew.pagination.firstPage")}
          </button>

          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={atFirstPage || isLoading}
            className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            &lt;
          </button>

          {pageItems.map((page, idx) =>
            page === "..." ? (
              <span key={`dots-${idx}`} className="px-1 text-sm text-gray-400">
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                disabled={isLoading}
                className={`min-w-[28px] px-2 py-1 text-sm transition-colors ${
                  page === currentPage
                    ? "text-gray-900 font-semibold underline underline-offset-4"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {page + 1}
              </button>
            ),
          )}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={atLastPage || isLoading}
            className="px-2 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            &gt;
          </button>

          <button
            onClick={() => onPageChange(totalPages - 1)}
            disabled={atLastPage || isLoading}
            className="text-sm text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {t("statsNew.pagination.lastPage")}
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
